import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoConnect10 from '../../assets/logo-connect10.png';

const AboutModal = ({ isOpen, onClose, userName }) => {
    const [clickCount, setClickCount] = useState(0);
    const [showPong, setShowPong] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setClickCount(0);
            setShowPong(false);
            setToastMessage('');
        }
    }, [isOpen]);

    const handleAuthorClick = () => {
        if (showPong) return;

        const newCount = clickCount + 1;
        setClickCount(newCount);

        const stepsLeft = 7 - newCount;

        if (stepsLeft > 0) {
            setToastMessage(`Você está a ${stepsLeft} passos de uma descoberta`);
            // Limpar o toast após 2 segundos
            setTimeout(() => setToastMessage(''), 2000);
        } else {
            setToastMessage('');
            setShowPong(true);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col relative"
                        style={{ maxHeight: '90vh' }}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700">
                            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">{showPong ? 'Router Pong' : 'Sobre'}</h2>
                            <button
                                onClick={onClose}
                                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                                aria-label="Fechar"
                                title="Fechar"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                            {showPong ? (
                                <PongGame userName={userName} />
                            ) : (
                                <div className="space-y-6">
                                    <div className="flex justify-center mb-6">
                                        <img src={logoConnect10} alt="Logo Connect10" className="h-16" />
                                    </div>

                                    <div className="text-sm text-slate-600 dark:text-slate-300 space-y-4">
                                        <p>
                                            O Connect10 App é uma plataforma de gerenciamento de rede e serviços,
                                            desenvolvida para otimizar as operações e melhorar o atendimento aos nossos clientes.
                                        </p>

                                        <div>
                                            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">Tecnologias Utilizadas</h3>
                                            <ul className="list-disc pl-5 space-y-1">
                                                <li>React & Tailwind CSS (Frontend)</li>
                                                <li>Node.js & Express (Backend)</li>
                                                <li>Firebase Auth & Firestore (Banco de Dados e Autenticação)</li>
                                                <li>Integração com Ozmap API e Google Maps API</li>
                                            </ul>
                                        </div>

                                        <div>
                                            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">Licenças de Software</h3>
                                            <p>Este software utiliza bibliotecas de código aberto. Todos os direitos reservados aos seus respectivos autores e mantenedores conforme suas licenças (MIT, Apache 2.0, entre outras).</p>
                                        </div>

                                        <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
                                            <p>
                                                <strong>Empresa:</strong> CONNECT10 INTERNET LTDA
                                            </p>
                                            <p>
                                                <strong>E-mail de suporte:</strong> <a href="mailto:redefibra@connect10.com.br" className="text-blue-600 dark:text-blue-400 hover:underline">redefibra@connect10.com.br</a>
                                            </p>
                                            <p>
                                                <strong>Autor:</strong>{' '}
                                                <span
                                                    onClick={handleAuthorClick}
                                                    className="select-none"
                                                >
                                                    Fernando J. Cordeiro
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {!showPong && (
                            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-center">
                                <a
                                    href="/privacidade"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-2 py-1"
                                >
                                    <span>Política de Privacidade</span>
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                            </div>
                        )}

                        {/* Toast */}
                        <AnimatePresence>
                            {toastMessage && (
                                <motion.div
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 50 }}
                                    className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white px-4 py-2 rounded-full shadow-lg text-sm z-50 pointer-events-none"
                                >
                                    {toastMessage}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

// Pong Game Component
const PongGame = ({ userName }) => {
    const canvasRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const [score, setScore] = useState({ player: 0, ai: 0 });

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;

        // Sound Effects (Web Audio API)
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();

        const playSound = (type) => {
            if (audioCtx.state === 'suspended') {
                audioCtx.resume();
            }
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);

            if (type === 'hit') {
                oscillator.type = 'square';
                oscillator.frequency.setValueAtTime(440, audioCtx.currentTime); // A4
                oscillator.frequency.exponentialRampToValueAtTime(880, audioCtx.currentTime + 0.1);
                gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.1);
                oscillator.start();
                oscillator.stop(audioCtx.currentTime + 0.1);
            } else if (type === 'score') {
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(300, audioCtx.currentTime);
                oscillator.frequency.exponentialRampToValueAtTime(150, audioCtx.currentTime + 0.3);
                gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
                oscillator.start();
                oscillator.stop(audioCtx.currentTime + 0.3);
            }
        };

        // Game State
        const createRouterState = () => {
            return {
                leds: [
                    { on: false, nextToggle: Date.now() + Math.random() * 500 },
                    { on: false, nextToggle: Date.now() + Math.random() * 500 },
                    { on: false, nextToggle: Date.now() + Math.random() * 500 }
                ],
                fastBlinkUntil: 0,
                redUntil: 0
            };
        };

        const state = {
            width: canvas.width,
            height: canvas.height,
            paddleWidth: 10,
            paddleHeight: 60,
            ballSize: 10, // radius
            playerY: canvas.height / 2 - 30,
            aiY: canvas.height / 2 - 30,
            ballX: canvas.width / 2,
            ballY: canvas.height / 2,
            ballDX: 4 * (Math.random() > 0.5 ? 1 : -1),
            ballDY: 4 * (Math.random() > 0.5 ? 1 : -1),
            playerScore: 0,
            aiScore: 0,
            keys: { ArrowUp: false, ArrowDown: false },
            leftRouter: createRouterState(),
            rightRouter: createRouterState()
        };

        // Update LEDs
        const updateLeds = (routerState, now) => {
            const isFastBlinking = now < routerState.fastBlinkUntil;
            const isRed = now < routerState.redUntil;

            for (let i = 0; i < 3; i++) {
                if (now > routerState.leds[i].nextToggle) {
                    routerState.leds[i].on = !routerState.leds[i].on;
                    const minDelay = isFastBlinking || isRed ? 50 : 100;
                    const maxDelay = isFastBlinking || isRed ? 150 : 400;
                    routerState.leds[i].nextToggle = now + minDelay + Math.random() * (maxDelay - minDelay);
                }
            }
        };

        // Draw Wi-Fi Signal
        const drawWiFi = (x, y, radius) => {
            ctx.strokeStyle = '#3b82f6'; // Blue
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';

            // Dot
            ctx.beginPath();
            ctx.arc(x, y + radius/2, radius/4, 0, Math.PI * 2);
            ctx.fillStyle = '#3b82f6';
            ctx.fill();

            // Arcs
            ctx.beginPath();
            ctx.arc(x, y + radius/2, radius, Math.PI * 1.25, Math.PI * 1.75);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(x, y + radius/2, radius * 1.6, Math.PI * 1.25, Math.PI * 1.75);
            ctx.stroke();
        };

        // Draw Router
        const drawRouter = (x, y, width, height, isRight, routerState) => {
            // Base bar
            ctx.fillStyle = '#64748b'; // Slate 500
            ctx.fillRect(x, y, width, height);

            // Router body behind/on the bar
            const rWidth = 20;
            const rHeight = 30;
            const rX = isRight ? x - rWidth + width : x;
            const rY = y + height/2 - rHeight/2;

            ctx.fillStyle = '#1e293b'; // Slate 800
            ctx.fillRect(rX, rY, rWidth, rHeight);

            // Antennas
            ctx.strokeStyle = '#1e293b';
            ctx.lineWidth = 2;
            ctx.beginPath();
            if (isRight) {
                ctx.moveTo(rX + rWidth/2, rY);
                ctx.lineTo(rX + rWidth/2 - 10, rY - 10);

                ctx.moveTo(rX + rWidth/2, rY + rHeight);
                ctx.lineTo(rX + rWidth/2 - 10, rY + rHeight + 10);
            } else {
                ctx.moveTo(rX + rWidth/2, rY);
                ctx.lineTo(rX + rWidth/2 + 10, rY - 10);

                ctx.moveTo(rX + rWidth/2, rY + rHeight);
                ctx.lineTo(rX + rWidth/2 + 10, rY + rHeight + 10);
            }
            ctx.stroke();

            // Lights
            const now = Date.now();
            const isRed = now < routerState.redUntil;

            for (let i = 0; i < 3; i++) {
                if (routerState.leds[i].on) {
                    ctx.fillStyle = isRed ? '#ef4444' : '#22c55e'; // Red if penalty, else Green
                } else {
                    ctx.fillStyle = '#0f172a'; // Off state (dark slate)
                }
                const ledY = rY + 5 + (i * 7);
                ctx.fillRect(rX + (isRight ? 4 : rWidth - 8), ledY, 4, 4);
            }
        };

        const draw = () => {
            // Clear
            ctx.fillStyle = '#f8fafc'; // light slate
            // Check dark mode roughly (assuming body has dark class or we just use a neutral dark-ish blue for game area)
            const isDark = document.documentElement.classList.contains('dark');
            ctx.fillStyle = isDark ? '#0f172a' : '#f1f5f9';
            ctx.fillRect(0, 0, state.width, state.height);

            // Center line
            ctx.setLineDash([5, 15]);
            ctx.beginPath();
            ctx.moveTo(state.width / 2, 0);
            ctx.lineTo(state.width / 2, state.height);
            ctx.strokeStyle = isDark ? '#334155' : '#cbd5e1';
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.setLineDash([]);

            // Paddles
            drawRouter(10, state.playerY, state.paddleWidth, state.paddleHeight, false, state.leftRouter);
            drawRouter(state.width - 10 - state.paddleWidth, state.aiY, state.paddleWidth, state.paddleHeight, true, state.rightRouter);

            // Ball
            drawWiFi(state.ballX, state.ballY, state.ballSize);
        };

        const update = () => {
            const now = Date.now();
            updateLeds(state.leftRouter, now);
            updateLeds(state.rightRouter, now);

            // Player movement
            if (state.keys.ArrowUp && state.playerY > 0) {
                state.playerY -= 5;
            }
            if (state.keys.ArrowDown && state.playerY < state.height - state.paddleHeight) {
                state.playerY += 5;
            }

            // Touch support
            if (state.touchY !== undefined) {
                 const targetY = state.touchY - state.paddleHeight / 2;
                 // Interpolate for smooth movement
                 state.playerY += (targetY - state.playerY) * 0.2;
                 // Constrain
                 state.playerY = Math.max(0, Math.min(state.height - state.paddleHeight, state.playerY));
            }

            // AI movement (simple prediction with some delay/imperfection)
            const aiCenter = state.aiY + state.paddleHeight / 2;
            // Only move if ball is moving towards AI
            if (state.ballDX > 0) {
                if (aiCenter < state.ballY - 10) {
                    state.aiY += 4;
                } else if (aiCenter > state.ballY + 10) {
                    state.aiY -= 4;
                }
            } else {
                 // Return to center slowly
                 if (aiCenter < state.height/2 - 5) state.aiY += 2;
                 else if (aiCenter > state.height/2 + 5) state.aiY -= 2;
            }
            state.aiY = Math.max(0, Math.min(state.height - state.paddleHeight, state.aiY));

            // Ball movement
            state.ballX += state.ballDX;
            state.ballY += state.ballDY;

            // Wall collision (top/bottom)
            if (state.ballY - state.ballSize < 0 || state.ballY + state.ballSize > state.height) {
                state.ballDY = -state.ballDY;
                // Keep ball within bounds to avoid sticking
                state.ballY = state.ballY - state.ballSize < 0 ? state.ballSize : state.height - state.ballSize;
            }

            // Paddle collision
            const hitPlayer = state.ballX - state.ballSize < 10 + state.paddleWidth &&
                              state.ballY > state.playerY &&
                              state.ballY < state.playerY + state.paddleHeight;

            const hitAI = state.ballX + state.ballSize > state.width - 10 - state.paddleWidth &&
                          state.ballY > state.aiY &&
                          state.ballY < state.aiY + state.paddleHeight;

            if (hitPlayer || hitAI) {
                playSound('hit');
                state.ballDX = -state.ballDX;

                // Fast blink on hit
                if (hitPlayer) {
                    state.leftRouter.fastBlinkUntil = Date.now() + 1000;
                } else {
                    state.rightRouter.fastBlinkUntil = Date.now() + 1000;
                }
                // Speed up slightly
                state.ballDX *= 1.05;
                state.ballDY *= 1.05;

                // Add spin based on where it hit the paddle
                const paddleY = hitPlayer ? state.playerY : state.aiY;
                const hitPoint = state.ballY - (paddleY + state.paddleHeight / 2);
                state.ballDY += hitPoint * 0.1;

                // Adjust position to prevent sticking
                if (hitPlayer) {
                    state.ballX = 10 + state.paddleWidth + state.ballSize;
                } else {
                    state.ballX = state.width - 10 - state.paddleWidth - state.ballSize;
                }
            }

            // Scoring
            if (state.ballX < 0) {
                state.aiScore++;
                playSound('score');
                state.leftRouter.redUntil = Date.now() + 1000;
                resetBall(-1);
            } else if (state.ballX > state.width) {
                state.playerScore++;
                playSound('score');
                state.rightRouter.redUntil = Date.now() + 1000;
                resetBall(1);
            }

            setScore({ player: state.playerScore, ai: state.aiScore });
        };

        const resetBall = (direction) => {
            state.ballX = state.width / 2;
            state.ballY = state.height / 2;
            state.ballDX = 4 * direction;
            state.ballDY = 4 * (Math.random() > 0.5 ? 1 : -1);
        };

        const loop = () => {
            update();
            draw();
            animationFrameId = requestAnimationFrame(loop);
        };

        // Resize handler
        const handleResize = () => {
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                canvas.width = clientWidth;
                canvas.height = clientHeight;
                state.width = clientWidth;
                state.height = clientHeight;
                // Reset ball and paddles
                state.playerY = state.height / 2 - state.paddleHeight / 2;
                state.aiY = state.height / 2 - state.paddleHeight / 2;
                resetBall(state.ballDX > 0 ? 1 : -1);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial sizing

        // Keyboard listeners
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault(); // Prevent scrolling
                state.keys[e.key] = true;
            }
        };
        const handleKeyUp = (e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') state.keys[e.key] = false;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        // Touch listeners
        const handleTouchMove = (e) => {
            if (e.touches.length > 0 && containerRef.current) {
                const rect = canvas.getBoundingClientRect();
                state.touchY = e.touches[0].clientY - rect.top;
                e.preventDefault(); // Prevent scrolling
            }
        };
        const handleTouchEnd = () => {
             state.touchY = undefined;
        };

        canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
        canvas.addEventListener('touchend', handleTouchEnd);
        canvas.addEventListener('touchstart', handleTouchMove, { passive: false });

        // Mouse listeners (for testing/desktop)
        const handleMouseMove = (e) => {
             if (containerRef.current) {
                const rect = canvas.getBoundingClientRect();
                state.touchY = e.clientY - rect.top;
             }
        };
        const handleMouseLeave = () => {
            state.touchY = undefined;
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);


        loop();

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            canvas.removeEventListener('touchmove', handleTouchMove);
            canvas.removeEventListener('touchend', handleTouchEnd);
            canvas.removeEventListener('touchstart', handleTouchMove);
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);

    const firstName = userName ? userName.split(' ')[0] : 'Jogador';

    return (
        <div className="flex flex-col h-full w-full">
            <div className="flex justify-between items-center mb-4 px-8 text-2xl font-bold text-slate-700 dark:text-slate-300">
                <span>{score.player}</span>
                <span className="text-sm font-normal text-slate-500">{firstName} vs IA</span>
                <span>{score.ai}</span>
            </div>
            <div
                ref={containerRef}
                className="flex-1 w-full bg-slate-100 dark:bg-slate-900 rounded-lg overflow-hidden relative cursor-crosshair border border-slate-300 dark:border-slate-700"
                style={{ minHeight: '300px' }}
            >
                <canvas ref={canvasRef} className="block w-full h-full" />
                <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-slate-400 pointer-events-none">
                    Use as setas para cima/baixo, toque ou mouse para mover
                </div>
            </div>
        </div>
    );
};

export default AboutModal;
